import styles from './ProjectTimeMask.module.scss';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { Checkbox, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import clsx from 'clsx';
import i18next from 'i18next';
import { DateTime, Duration } from 'luxon';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';

import {
  decimalToDuration,
  LABEL_VISIBILITY,
  MaskActionButton,
} from '@work4all/components';
import {
  MultiStepControls,
  Step,
} from '@work4all/components/lib/input/multi-step-controls';
import { TimeInputPicker } from '@work4all/components/lib/input/time-input-picker';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import {
  IUser,
  useDataMutation,
  useFormPlus,
  useStopwatch,
  useUser,
} from '@work4all/data';
import { useCustomFieldsConfig } from '@work4all/data/lib/custom-fields';
import { useEntityJsonSchema } from '@work4all/data/lib/json-schema/EntityJsonSchemasContext';

import {
  checkUserRight,
  UserRights,
} from '@work4all/models/lib/additionalEnums/UserRights';
import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { CustomField } from '@work4all/models/lib/Classes/CustomField.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { ProjectProcess } from '@work4all/models/lib/Classes/ProjectProcess.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { TimeTracking } from '@work4all/models/lib/Classes/TimeTracking.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { ArticleKind } from '@work4all/models/lib/Enums/ArticleKind.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';
import { TicketStatus } from '@work4all/models/lib/Enums/TicketStatus.enum';
import { TimeTrackingKind } from '@work4all/models/lib/Enums/TimeTrackingKind.enum';

import { useJSONSchemaResolver } from '@work4all/utils';
import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';
import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';
import {
  canAddTimeTrackingForProjects,
  canEditTimeTrackingForOthers,
  canViewERPPrices,
} from '@work4all/utils/lib/permissions';

import { ArticlePickerField } from '../../components/entity-picker/ArticlePickerField';
import { ContractPickerField } from '../../components/entity-picker/ContractPickerField';
import { CustomerPickerField } from '../../components/entity-picker/CustomerPickerField';
import { PositionPickerField } from '../../components/entity-picker/PositionPickerField';
import { ProjectPickerField } from '../../components/entity-picker/ProjectPickerField';
import { ProjectProcessPickerField } from '../../components/entity-picker/ProjectProcessPickerField';
import { TicketPickerField } from '../../components/entity-picker/TicketPickerField';
import { UserPickerField } from '../../components/entity-picker/UserPickerField';
import { usePositionDone } from '../../hooks/use-position-done';
import { usePositionDoneMutation } from '../../hooks/use-position-done/use-position-done-mutation';
import { useTimeTrackingTemplates } from '../../hooks/use-time-tracking-templates';
import { settings, useSetting } from '../../settings';
import { TimeTrackingRestrictionType } from '../../settings/settings';
import { ApiErrors, ValidationErrors } from '../apollo/ValidationErrors';
import { ControllerPlus } from '../mask-overlays/form-plus/controller-plus';
import { useFormContextPlus } from '../mask-overlays/form-plus/use-form-context-plus';
import {
  DateTimeInputPicker,
  LabeledInput,
  MenuItem,
} from '../mask-overlays/locked-inputs';
import {
  Collapse,
  ControlWrapper,
  Form,
  MaskOverlayHeader,
} from '../mask-overlays/mask-overlay/components';
import { CustomFields } from '../mask-overlays/mask-overlay/components/custom-fields/CustomFields';
import { normalizeCustomFields } from '../mask-overlays/mask-overlay/components/custom-fields/normalize-custom-fields';
import { prepareInputWithCustomFields } from '../mask-overlays/mask-overlay/components/custom-fields/prepare-input-with-custom-fields';
import { WithCustomFields } from '../mask-overlays/mask-overlay/components/custom-fields/with-custom-fields';
import { MaskActions } from '../mask-overlays/mask-overlay/components/MaskActions';
import { MaskOverlayDeleteButton } from '../mask-overlays/mask-overlay/components/MaskOverlayDeleteButton';
import { MaskOverlayMenuWrapper } from '../mask-overlays/mask-overlay/components/MaskOverlayMenuWrapper';
import { MaskOverlaySubmitButton } from '../mask-overlays/mask-overlay/components/MaskOverlaySubmitButton';
import {
  MaskContextProvider,
  useMaskConfig,
  useMaskContext,
  useMaskContextValue,
} from '../mask-overlays/mask-overlay/hooks/mask-context';
import { useConfirmDeleteEntities } from '../mask-overlays/mask-overlay/hooks/use-confirm-delete-entities';
import { useConfirmBeforeCloseMask } from '../mask-overlays/mask-overlay/hooks/use-confrm-before-close-mask';
import {
  EntityRightsContext,
  useEntityRightsContext,
} from '../mask-overlays/mask-overlay/hooks/use-entity-rights';
import { useStandardDeleteEntityHandler } from '../mask-overlays/mask-overlay/hooks/use-standard-delete-entity-handler';
import { NEW_ENTITY_ID } from '../mask-overlays/mask-overlay/mask-metadata';
import { useFormUpdate } from '../mask-overlays/mask-overlay/utils/use-form-update';
import { ProjectTimeSettings } from '../mask-overlays/mask-overlay/views/settings/project-time-settings/ProjectTimeSettings';

import DurationField from './components/DurationField';
import { useTimeTrackingRestriction } from './hooks/use-time-tracking-restriction';
import { StopwatchIconButton } from './StopwatchIconButton';

type TimeTrackingFormValue = PathsOf<
  WithCustomFields<
    TimeTracking & {
      users?: User[];
      shouldMarkPositionDone?: boolean;
      isTemplate?: boolean;
    }
  >,
  3
>;

export interface ProjectTimeMaskInit {
  /**
   * @default TimeTrackingKind.ANZAHL
   */
  kind?: TimeTrackingKind.ANZAHL | TimeTrackingKind.VON_BIS;
  /**
   * @default new Date() // Current time
   */
  date?: Date;
  /**
   * Defaults to 1 hour before `date` or 1 hour ago if `date` is omitted.
   */
  startDateTime?: Date;
  /**
   * Defaults to the same value as `date` or current time if `date` is omitted.
   */
  endDateTime?: Date;
  /**
   * @default 0
   */
  amount?: number;
  /**
   * @default 0
   */
  pause?: number;

  id?: number;
  projectProcess?: ProjectProcess;
  project?: Project;
  customer?: Customer;
  ticket?: Ticket;
  comment?: string;
  contract?: Contract;
  activityArticle?: Article;
  activityArticleId?: number;
  users?: User[];
  position?: Position;
  customFieldList?: CustomField[];
}

export interface ProjectTimeMaskProps {
  user?: IUser | User;
  item?: TimeTracking | null;
  init?: ProjectTimeMaskInit;
  amplitudeEntryPoint?: string;
  onCompleted?: () => void;
  createdFrom?: Entities;
  onCopy?: (item: TimeTracking) => void;
  createdFromId?: number;
}

const getDuration = (startTime: string, endTime: string, pauseTime: number) => {
  const startDateTime = DateTime.fromISO(startTime).startOf('minute');
  const endDateTime = DateTime.fromISO(endTime).startOf('minute').set({
    year: startDateTime.year,
    month: startDateTime.month,
    day: startDateTime.day,
  });

  if (!startDateTime.isValid || !endDateTime.isValid) {
    return Duration.fromObject({
      hours: 0,
    });
  }

  const pause = pauseTime || 0;
  return Duration.fromMillis(
    endDateTime.toMillis() - startDateTime.toMillis()
  ).minus({ minutes: pause });
};

export function ProjectTimeMask(props: ProjectTimeMaskProps) {
  const {
    item = null,
    amplitudeEntryPoint,
    createdFrom,
    onCopy,
    createdFromId,
  } = props;

  const currentUser = useUser();
  const canEditAll = canEditTimeTrackingForOthers(currentUser, undefined);

  const user = props.user || currentUser;
  const { t } = useTranslation();

  const { confirm, goBack, setObjectionListener } = useHistoryStack();

  const onCompleted = () => {
    sendAmplitudeData(EventType.SaveProjectTime, {
      entryPoint: amplitudeEntryPoint,
    });
    setObjectionListener(null);
    goBack({ force: true });
    props.onCompleted?.();
  };

  const [errors, setErrors] = useState<ApiErrors<TimeTrackingFormValue>>([]);
  const [mutate] = useDataMutation({
    entity: Entities.timeTracking,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
      userId: null,
    },
    onCompleted: onCompleted,
    onError: (e) => {
      const parsedErrors = ValidationErrors.parseErrors<TimeTrackingFormValue>(
        e.graphQLErrors,
        Entities.timeTracking
      );
      setErrors(parsedErrors);
    },
  });

  useEffect(() => {
    if (item === null) {
      sendAmplitudeData(EventType.AddProjectTime, {
        entryPoint: amplitudeEntryPoint,
      });
    } else {
      sendAmplitudeData(EventType.EditProjectTime, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  }, [amplitudeEntryPoint, item]);

  const projectTimeTrackingMarkItemsAsDeductableAutomatically = useSetting(
    settings.projectTimeTrackingMarkItemsAsDeductableAutomatically()
  );

  const projectTimeTrackingDeductableByActivity = useSetting(
    settings.projectTimeTrackingDeductableByActivity()
  );

  const projectTimeTrackingDeductableWithoutActivity = useSetting(
    settings.projectTimeTrackingDeductableWithoutActivity()
  );

  const { value: createTimeTrackingForOthersValue } = useSetting(
    settings.createTimeTrackingForOthers()
  );

  const canCreateTimeTrackingForOthers =
    createdFrom === Entities.appointment
      ? createTimeTrackingForOthersValue
      : true;

  const { type: restrictionType, backdatedLimit } =
    useTimeTrackingRestriction();

  const { enqueueSnackbar } = useSnackbar();
  const stopwatch = useStopwatch();

  const templates = useTimeTrackingTemplates();
  const confirmDeleteTemplate = useConfirmDeleteEntities({ standard: true });

  const handleDeleteTemplate = useCallback(
    async (id: number) => {
      const confirmed = await confirmDeleteTemplate();

      if (confirmed) {
        templates.remove(id);
        goBack({ force: true });
      }
    },
    [confirmDeleteTemplate, templates, goBack]
  );

  const timeTrackingTemplateHandler = useCallback(
    (
      id: number,
      isAlreadyATemplate: boolean,
      values: TimeTrackingFormValue
    ) => {
      if (!id) {
        return;
      }

      if (isAlreadyATemplate === values.isTemplate) {
        return;
      }

      if (!values.isTemplate) {
        templates.remove(id);
        return;
      }

      templates.add({
        id: id,
        project: values.project,
        projectProcess: values.projectProcess,
        customer: values.customer,
        contract: values.contract,
        position: values.position,
        ticket: values.ticket,
        activityArticle: values.activityArticle,
        comment: values.comment,
      });
    },
    [templates]
  );

  const onSubmit = useEventCallback(
    async (values: TimeTrackingFormValue, isStopwatchMode = false) => {
      const showRestrictionError = () => {
        enqueueSnackbar(
          t('ERROR.TIME_TRACKING.BACKDATED_CREATION_NOT_ALLOWED'),
          {
            variant: 'error',
            autoHideDuration: 5000,
          }
        );
      };

      if (restrictionType === TimeTrackingRestrictionType.RESTRICTED) {
        showRestrictionError();
        return;
      }

      const date = DateTime.fromISO(
        values.timeTrackingKind === TimeTrackingKind.VON_BIS
          ? values.startDateTime
          : values.date
      ).startOf('day');

      const creationAllowedUntil = DateTime.now()
        .startOf('day')
        .minus({
          days: backdatedLimit ?? 0,
        });

      if (
        restrictionType === TimeTrackingRestrictionType.BACKDATED_LIMIT &&
        date.toMillis() < creationAllowedUntil.toMillis()
      ) {
        enqueueSnackbar(
          t('ERROR.TIME_TRACKING.HISTORICAL_EDIT_DEADLINE', {
            date: creationAllowedUntil.toLocaleString(
              DateTimeCustom.DATE_SHORT
            ),
          }),

          {
            variant: 'error',
            autoHideDuration: 5000,
          }
        );
        return;
      }

      // date is always send without timezoneinfo
      values.date = date.toFormat('yyyy-MM-dd');
      //make sure that we only send the proper date fields
      if (values.timeTrackingKind === TimeTrackingKind.VON_BIS) {
        //autofill the amount
        values.amount = Duration.fromMillis(
          DateTime.fromISO(values.endDateTime)
            .startOf('minute')
            .toJSDate()
            .getTime() -
            DateTime.fromISO(values.startDateTime)
              .startOf('minute')
              .toJSDate()
              .getTime() -
            values.pause * 60 * 1000
        )
          .shiftTo('hours')
          .toObject().hours;
      } else {
        values.startDateTime = null;
        values.endDateTime = null;
      }

      values = prepareInputWithCustomFields(values, customFields);

      let responses = [];

      if (!values.id) {
        values.isBillable =
          projectTimeTrackingMarkItemsAsDeductableAutomatically.value ||
          (projectTimeTrackingDeductableByActivity.value &&
            values.activityArticle &&
            !values.activityArticle?.notdeductable) ||
          (!values.activityArticleId &&
            projectTimeTrackingDeductableWithoutActivity.value);
      }

      if (isStopwatchMode) {
        const currentDateTime = DateTime.now();
        const dateValue = DateTime.fromISO(values.date);
        const startTime = dateValue
          .set({
            hour: currentDateTime.hour,
            minute: currentDateTime.minute,
            second: currentDateTime.second,
            millisecond: currentDateTime.millisecond,
          })
          .toISO();

        await stopwatch.start({
          data: {
            projectCode: values.projectId,
            startTime: startTime,
            articleWorkItemCode: values.activityArticleId ?? 0,
            customerCode: values.customer?.id,
            projectStepCode: values.projectProcessId,
            orderConfirmationCode: values.contract?.id,
            orderConfirmationPositionCode: values.position?.id,
            ticketId: values.ticket?.id,
            note: values.comment,
          },
        });
        onCompleted();
      } else if (canEditAll && canCreateTimeTrackingForOthers && values.users) {
        const multiple = values.users
          .map((x) => ({ ...values, userId: x.id }))
          .map((x) => mutate(x));
        const multipleResponses = await Promise.all(multiple);
        responses.push(...multipleResponses);
      } else {
        const response = await mutate(values);
        responses.push(response);
      }

      responses = responses.filter(Boolean);

      const id = responses.find(
        (response) => response.userId === currentUser.benutzerCode
      )?.id;

      timeTrackingTemplateHandler(id, isTimeTrackingTemplate, values);

      // Handle position done after successful save
      if (values.position && positionDoneActions.length > 0) {
        if (
          !defaultValues.shouldMarkPositionDone &&
          values.shouldMarkPositionDone
        ) {
          // Open the position done dialog
          positionDoneActions[0].handler(values.position.id);
        } else if (
          defaultValues.shouldMarkPositionDone &&
          !values.shouldMarkPositionDone
        ) {
          onPositionDone({
            position: values.position,
            user: values.user,
            toRemoveDone: true,
            /**
             * Temporary workaround: Backend requires `completionDate` and `notes` even when not needed.
             * This will be removed with WW-7703.
             */
            completionDate: DateTime.now().toISO(),
            notes: '',
          });
        }
      }
    }
  );

  const projectTimeTrackingCustomerMandatory = useSetting(
    settings.projectTimeTrackingCustomerMandatory()
  );
  const projectTimeTrackingTicketMandatory = useSetting(
    settings.projectTimeTrackingTicketMandatory()
  );
  const projectTimeTrackingProjectMandatory = useSetting(
    settings.projectTimeTrackingProjectMandatory()
  );
  const projectTimeTrackingActivityMandatory = useSetting(
    settings.projectTimeTrackingActivityMandatory()
  );
  const projectTimeTrackingContractMandatory = useSetting(
    settings.projectTimeTrackingContractMandatory()
  );
  const projectTimeTrackingPositionMandatory = useSetting(
    settings.projectTimeTrackingPositionMandatory()
  );
  const projectTimeTrackingProjectProcessMandatory = useSetting(
    settings.projectTimeTrackingProjectProcessMandatory()
  );
  const projectProcessOverBookingNotPossible = useSetting(
    settings.projectProcessOverBookingNotPossible()
  );

  const isTimeTrackingTemplate = useMemo(() => {
    if (!item || !item.id) return false;

    return templates.includes(item.id);
  }, [item, templates]);

  const isCreatedFromTimeTrackingTemplate = useMemo(() => {
    if (createdFrom !== Entities.timeTracking || !createdFromId) {
      return false;
    }

    return templates.includes(createdFromId);
  }, [createdFromId, createdFrom, templates]);

  const defaultValuesRaw = useMemo(() => {
    if (item == null) {
      const timeTrackingKind: TimeTrackingKind =
        props.init?.kind ??
        (localStorage.getItem('TIME_TRACKING_KIND_LS_KEY') ===
        TimeTrackingKind.VON_BIS
          ? TimeTrackingKind.VON_BIS
          : TimeTrackingKind.ANZAHL);

      const currentDate = DateTime.now().set({
        second: 0,
        millisecond: 0,
      });
      const date = props.init?.date ?? currentDate.toJSDate();
      const dateToDateTime = DateTime.fromJSDate(date);
      const startDateTime = date;
      const endDateTime = dateToDateTime.plus({ minutes: 1 }).toJSDate();
      const init: Omit<
        ProjectTimeMaskInit,
        | 'comment'
        | 'project'
        | 'customer'
        | 'projectProcess'
        | 'ticket'
        | 'contract'
      > = {
        kind: timeTrackingKind,
        date: date,
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        amount: 0,
        pause: 0,
        ...props.init,
      };

      const formValue: TimeTrackingFormValue = {
        date: DateTime.fromJSDate(init.date).toISO(),
        startDateTime: DateTime.fromJSDate(init.startDateTime).toISO(),
        endDateTime: DateTime.fromJSDate(init.endDateTime).toISO(),
        pause: init.pause,
        amount: init.amount,
        timeTrackingKind: init.kind,
        customer: props.init?.customer,
        businessPartnerId: props.init?.customer?.id,
        user: {
          id: (user as IUser).benutzerCode || (user as User).id,
          displayName: user.displayName,
        },
        userId: (user as IUser).benutzerCode || (user as User).id,
        users: props.init?.users ?? [
          {
            id: (user as IUser).benutzerCode || (user as User).id,
            displayName: user.displayName,
          },
        ],
        project: props.init?.project,
        projectId: props.init?.project?.id,
        projectProcess: props.init?.projectProcess,
        projectProcessId: props.init?.projectProcess?.id,
        contract: props.init?.contract,
        activityArticle: props.init?.activityArticle,
        activityArticleId: props.init?.activityArticleId,
        bZObjMemberId: props.init?.contract?.id,
        ticket: props.init?.ticket,
        ticketNumber: props.init?.ticket?.number,
        comment: props.init?.comment || '',
        position: props.init?.position,
        positionId: props.init?.position?.id,
        shouldMarkPositionDone: props.init?.position?.isFinished,
        customFieldList: init.customFieldList,
        isTemplate: false,
      };
      return formValue;
    }

    // DateTimeInputPicker does not work with the format returned by the API for
    // some reason.
    const toISO = (date: string | null): string | null => {
      if (date == null) {
        return null;
      }

      return DateTime.fromISO(date).toISO();
    };

    const startDateTime = toISO(item.startDateTime);
    const endDateTime = toISO(item.endDateTime);

    return {
      ...item,
      __typename: undefined,
      date: DateTime.fromFormat(item.date, 'yyyy-MM-dd').toISO(),
      startDateTime,
      endDateTime,
      // A workaround for stopwatch tracked time
      timeTrackingKind:
        item.timeTrackingKind === TimeTrackingKind.OHNE_ANGABE &&
        startDateTime &&
        endDateTime
          ? TimeTrackingKind.VON_BIS
          : item.timeTrackingKind,
      shouldMarkPositionDone: item.position?.isFinished,
      isTemplate: isTimeTrackingTemplate,
    };
  }, [item, props.init, user, isTimeTrackingTemplate]);

  const customFields = useCustomFieldsConfig({ entity: Entities.timeTracking });

  const defaultValues = useMemo(() => {
    return normalizeCustomFields(defaultValuesRaw, customFields);
  }, [defaultValuesRaw, customFields]);

  const projectProcessError = useCallback(
    (
      data: Required<Pick<TimeTracking, 'id' | 'projectProcess' | 'amount'>>
    ) => {
      if (
        data?.projectProcess &&
        projectProcessOverBookingNotPossible.value &&
        !data?.projectProcess?.overBookingPossible
      ) {
        const usedAmount =
          data?.projectProcess?.timeTrackings?.length === 0
            ? 0
            : data?.projectProcess?.timeTrackings
                ?.filter((x) => x.id !== data.id)
                ?.map((x) => x.amount)
                .reduce((a, b) => a + b);

        const totalAmount = data.amount + usedAmount;
        if (totalAmount >= data?.projectProcess?.planningAmount * 8) {
          return {
            message: t('VALIDATION.PROJECT_PROCESS_NO_BUDGET'),
            type: 'customValidation',
          };
        }
      }

      if (
        data?.projectProcess &&
        data?.projectProcess?.completionFeedbacks?.[0]?.completionPercent ===
          100
      ) {
        return {
          message: t('VALIDATION.PROJECT_PROCESS_COMPLETED'),
          type: 'customValidation',
        };
      }

      return null;
    },
    [projectProcessOverBookingNotPossible.value, t]
  );

  const mask = useMaskConfig({
    entity: Entities.timeTracking,
    id: props?.item?.id || NEW_ENTITY_ID,
  });

  const skipAmountValidation = useRef(false);

  const customRules = useCallback(
    (data: TimeTracking) => {
      const errors = {};

      if (data.project?.projectStatus?.closedStatus) {
        errors['project'] = {
          message: t('VALIDATION.PROJECT_CLOSED'),
          type: 'customValidation',
        };
      }

      if (data.project?.lockInhouseServices) {
        errors['project'] = {
          message: t('VALIDATION.PROJECT_INHOUSE_SERVICE_LOCKED'),
          type: 'customValidation',
        };
      }

      const totalAmount =
        data.timeTrackingKind === TimeTrackingKind.VON_BIS
          ? getDuration(data.startDateTime, data.endDateTime, data.pause).as(
              'hours'
            )
          : data.amount;

      const projectProcessErrorValue = projectProcessError({
        id: data.id,
        projectProcess: data.projectProcess,
        amount: totalAmount ?? 0,
      });

      if (projectProcessErrorValue) {
        errors['projectProcess'] = projectProcessErrorValue;
      }

      if (mask.isCreateMode && data.contract?.isClosed) {
        errors['contract'] = {
          message: t('VALIDATION.CONTRACT_CLOSED'),
          type: 'customValidation',
        };
      }

      if (data.timeTrackingKind === TimeTrackingKind.VON_BIS) {
        // we need start and end date + times
        if (!data.startDateTime || !data.endDateTime) {
          errors['startDateTime'] = {
            message: t('VALIDATION.BOTH_TIMES_NEEDED'),
            type: 'customValidation',
          };
          errors['endDateTime'] = {
            message: t('VALIDATION.BOTH_TIMES_NEEDED'),
            type: 'customValidation',
          };
        }
      } else if (data.timeTrackingKind === TimeTrackingKind.ANZAHL) {
        // we need amount
        if (!data.amount && !skipAmountValidation.current) {
          errors['amount'] = {
            message: t('VALIDATION.TIME_MISSING'),
            type: 'customValidation',
            isMissing: true,
          };
        }
      }
      if (projectTimeTrackingCustomerMandatory.value && !data.customer) {
        errors['customer'] = {
          message: t('VALIDATION.CUSTOMER_MISSING'),
          type: 'customValidation',
          isMissing: true,
        };
      }
      if (projectTimeTrackingContractMandatory.value && !data.contract) {
        errors['contract'] = {
          message: t('VALIDATION.CONTRACT_MISSING'),
          type: 'customValidation',
          isMissing: true,
        };
      }
      if (projectTimeTrackingActivityMandatory.value && !data.activityArticle) {
        errors['activityArticle'] = {
          message: t('VALIDATION.ACTIVITY_MISSING'),
          type: 'customValidation',
          isMissing: true,
        };
      }
      if (projectTimeTrackingPositionMandatory.value && !data.position) {
        errors['position'] = {
          message: t('VALIDATION.POSITION_MISSING'),
          type: 'customValidation',
          isMissing: true,
        };
      }
      if (projectTimeTrackingProjectMandatory.value && !data.project) {
        errors['project'] = {
          message: t('VALIDATION.PROJECT_MISSING'),
          type: 'customValidation',
          isMissing: true,
        };
      }
      if (
        projectTimeTrackingProjectProcessMandatory.value &&
        !data.projectProcess
      ) {
        errors['projectProcess'] = {
          message: t('VALIDATION.PROJECTPROCESS_MISSING'),
          type: 'customValidation',
          isMissing: true,
        };
      }
      if (projectTimeTrackingTicketMandatory.value && !data.ticket) {
        errors['ticket'] = {
          message: t('VALIDATION.TICKET_MISSING'),
          type: 'customValidation',
          isMissing: true,
        };
      }

      return Object.keys(errors).length > 0 ? errors : true;
    },
    [
      projectProcessError,
      mask.isCreateMode,
      projectTimeTrackingCustomerMandatory.value,
      projectTimeTrackingContractMandatory.value,
      projectTimeTrackingActivityMandatory.value,
      projectTimeTrackingPositionMandatory.value,
      projectTimeTrackingProjectMandatory.value,
      projectTimeTrackingProjectProcessMandatory.value,
      projectTimeTrackingTicketMandatory.value,
      t,
    ]
  );

  const schema = useEntityJsonSchema(Entities.timeTracking);
  const resolver = useJSONSchemaResolver(schema, customRules);

  const form = useFormPlus<TimeTrackingFormValue>({
    resolver,
    defaultValues,
    mode: 'onChange',
    shouldFocusError: false,
    context: {
      schema,
    },
  });

  useEffect(() => {
    const lookup = {
      positionId: 'position',
    };
    errors.forEach((x) => {
      const property = Object.keys(lookup).includes(x.property)
        ? lookup[x.property]
        : x.property;
      form.setError(property, {
        message: ValidationErrors.translateError(x.code),
        type: 'apiValidation',
      });
    });
  }, [t, errors, form]);

  // We check if the project process coming from the props valid or not
  // if not valid we set the an error when opning the mask
  useEffect(() => {
    const projectProcessErrorValue = projectProcessError({
      id: props.init?.id,
      projectProcess: props.init?.projectProcess,
      amount: props.init?.amount ?? 0,
    });

    if (projectProcessErrorValue) {
      form.setError('projectProcess', projectProcessErrorValue);
    } else {
      form.setError('projectProcess', null);
    }
  }, [
    form,
    projectProcessError,
    props.init?.amount,
    props.init?.id,
    props.init?.projectProcess,
  ]);

  const { formState, getValues } = form;

  const editedDateTime = (
    startDateTime: string,
    endDateTime: string,
    returnThe: 'start' | 'end'
  ) => {
    const start = DateTime.fromISO(startDateTime).startOf('minute');
    const end = DateTime.fromISO(endDateTime).startOf('minute').set({
      year: start.year,
      month: start.month,
      day: start.day,
    });

    if (start.toMillis() > end.toMillis()) {
      if (start.toISODate() === end.toISODate()) {
        return returnThe === 'end' ? start : end;
      } else {
        return returnThe === 'end'
          ? start.set({
              hour: end.hour,
              minute: end.minute,
            })
          : end.set({
              hour: start.hour,
              minute: start.minute,
            });
      }
    }

    return returnThe === 'end' ? end : start;
  };

  const {
    value: shouldPrefillTimeTrackingCommentFromTicketProblemDescription,
  } = useSetting(
    settings.shouldPrefillTimeTrackingCommentFromTicketProblemDescription()
  );

  useFormUpdate(
    {
      startDateTime(startDateTime: string): TimeTrackingFormValue {
        const newEndDateTime = editedDateTime(
          startDateTime,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          getValues('endDateTime'),
          'end'
        ).toISO();

        return { endDateTime: newEndDateTime };
      },
      endDateTime(endDateTime: string): TimeTrackingFormValue {
        const newStartDateTime = editedDateTime(
          getValues('startDateTime'),
          endDateTime,
          'start'
        ).toISO();

        return { startDateTime: newStartDateTime };
      },
      activityArticle(value: Article): TimeTrackingFormValue {
        return {
          activityArticleId: value?.id || 0,
        };
      },
      user(value: User): TimeTrackingFormValue {
        return {
          userId: value?.id || 0,
        };
      },
      projectProcess(value: ProjectProcess): TimeTrackingFormValue {
        return { projectProcessId: value?.id || 0 };
      },

      customer(value: Customer): TimeTrackingFormValue {
        const customerId = value?.id;
        const result: { [key: string]: number | Ticket | Contract } = {
          businessPartnerId: customerId || 0,
        };

        return result;
      },
      ticket(value: Ticket): TimeTrackingFormValue {
        const result: {
          [key: string]:
            | number
            | Customer
            | Project
            | ProjectProcess
            | Contract
            | Position;
        } = {
          ticketNumber: value?.number || 0,
        };
        const customer = getValues('customer');
        const contract = getValues('contract');
        const project = getValues('project');

        if (
          !customer &&
          value?.businessPartner?.businessPartnerType === SdObjType.KUNDE
        ) {
          result.customer = value?.businessPartner?.data as Customer;
        }

        if (!project && value?.project) {
          result.project = value?.project;
          result.projectProcess = value?.projectProcess;
        }

        if (!contract && value?.contract) {
          result.contract = value?.contract;
          result.position = value?.position;
        }

        if (
          shouldPrefillTimeTrackingCommentFromTicketProblemDescription &&
          !result.comment &&
          value.problemDescription
        ) {
          result.comment = sanitizeHtml(value.problemDescription, {
            allowedTags: [],
          });
        }

        return result;
      },

      project(value: Project, prevProject: Project): TimeTrackingFormValue {
        const customer = getValues('customer');

        const result: { [key: string]: number | Customer | null } = {
          projectId: value?.id || 0,
        };

        if (value === null) {
          result.contract = null;
        }

        if (prevProject && prevProject?.id !== value?.id) {
          result.projectProcessId = 0;
          result.projectProcess = null;
          result.contract = null;
        }

        if (!customer && value?.customer) {
          result.customer = value?.customer;
        }

        return result;
      },

      contract(value: Contract, prevContract: Contract): TimeTrackingFormValue {
        const result: {
          [key: string]: number | Project | ProjectProcess | Customer;
        } = {
          bZObjMemberId: value?.id || 0,
        };

        if (prevContract && value?.id !== prevContract.id) {
          result.positionId = 0;
          result.position = null;
        }

        const customer = getValues('customer');

        if (!customer && value?.businessPartnerType === SdObjType.KUNDE) {
          result.customer = value?.businessPartnerContactCombined
            ?.businessPartner?.data as Customer;
        }

        if (value?.project) {
          result.project = value?.project;
          if (value?.projectProcess)
            result.projectProcess = value?.projectProcess;
        }

        return result;
      },
      position(value: Position): TimeTrackingFormValue {
        return {
          positionId: value?.id || 0,
          activityArticle: value?.article?.isActivity ? value.article : null,
          activityArticleId: value?.article?.isActivity ? value.article.id : 0,
          shouldMarkPositionDone: value?.isFinished,
        };
      },
    },
    form
  );

  useEffect(() => {
    form.reset(defaultValues, { keepErrors: true });
  }, [form, defaultValues]);

  const isDirtyForm = Object.entries(formState.dirtyFields).length > 0;

  useConfirmBeforeCloseMask(isDirtyForm);

  const { handleSubmit } = form;

  const deleteHandler = useStandardDeleteEntityHandler({
    entity: Entities.timeTracking,
    id: item?.id,
  });

  const [isSettingsOpen, setOpen] = useState(false);

  const onPositionDone = usePositionDoneMutation();

  // Position done hook setup - needs to be at main component level
  const positionDoneActions = usePositionDone({
    contract: form.watch('contract'),
    overrides: {
      user: form.watch('user'),
      position: form.watch('position'),
      date:
        form.watch('timeTrackingKind') === TimeTrackingKind.VON_BIS
          ? form.watch('startDateTime')
          : form.watch('date'),
    },
    onPositionDone,
  });
  const openSettings = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const maskContext = useMaskContextValue({ ...mask, customFields });

  const entityRights = useMemo(
    () => ({
      create: canAddTimeTrackingForProjects(currentUser),
      read: false, //ToDo there is no real read right, rather a visibility right
      update: canEditAll || canEditTimeTrackingForOthers(currentUser, item),
      delete: canEditAll || canEditTimeTrackingForOthers(currentUser, item),
    }),
    [canEditAll, currentUser, item]
  );

  const canSaveEntry =
    (entityRights.create && mask.mode === 'create' && isDirtyForm) ||
    (entityRights.create && mask.mode === 'create' && props.init) ||
    (mask.mode === 'edit' && entityRights.update && isDirtyForm);

  async function handleCopyClick() {
    if (!item) return;

    if (isDirtyForm) {
      const confirmed = await confirm(t('ALERTS.UNSAVED_CHANGES'));

      if (!confirmed) {
        return;
      }
    }

    onCopy(item);
  }

  const showMissingFieldsErrorSnackbar = useCallback(
    (errors) => {
      const fields = Object.keys(errors);
      const missingFields = fields.filter((field) => errors[field].isMissing);

      if (missingFields.length === 0) {
        return;
      }

      const sortedMissingFields = sortMissingFields(missingFields);
      const missingFieldsTranslated = sortedMissingFields.map((field) =>
        t(`TIME_TRACKING.FIELDS.${field.toUpperCase()}`)
      );
      const combinedMissingFields = getCombinedMissingFields(
        missingFieldsTranslated
      );

      enqueueSnackbar(
        t('TIME_TRACKING.MISSING_FIELDS_MESSAGE', {
          fields: combinedMissingFields,
          count: sortedMissingFields.length,
        }),
        {
          variant: 'error',
          autoHideDuration: 6000,
        }
      );
    },
    [enqueueSnackbar, t]
  );

  const saveHandler = useEventCallback(async () => {
    skipAmountValidation.current = false;
    await handleSubmit(onSubmit, showMissingFieldsErrorSnackbar)();
  });

  const startStopwatchHandler = useEventCallback(async () => {
    skipAmountValidation.current = true;
    await handleSubmit(
      (data) => onSubmit(data, true),
      showMissingFieldsErrorSnackbar
    )();
  });

  return (
    <>
      <NavigationOverlay
        classes={{
          wrapper: styles.dialogWrapper,
        }}
        open={isSettingsOpen}
        initialView={{
          view: <ProjectTimeSettings amplitudeEntryPoint="ProjectTimeMask" />,
          title: t('TIME_TRACKER.PROJECT_TIME'),
        }}
        close={() => {
          setOpen(false);
        }}
      />

      <MaskContextProvider value={maskContext}>
        <EntityRightsContext.Provider value={entityRights}>
          <FormProvider {...form}>
            <div className={clsx(styles.root, 'custom-scrollbar')}>
              <div>
                <Form noValidate>
                  <MaskOverlayHeader
                    title={t('TIME_TRACKER.PROJECT_TIME')}
                    actions={
                      <MaskActions>
                        {onCopy && mask.isEditMode ? (
                          <MaskActionButton
                            color="primary"
                            labelVisibility={LABEL_VISIBILITY.HIDE}
                            label={t('COMMON.COPY_CREATE')}
                            icon={<ContentCopyIcon />}
                            onClick={handleCopyClick}
                          />
                        ) : null}
                        <MaskOverlaySubmitButton
                          disabled={!canSaveEntry}
                          loading={formState.isSubmitting}
                          data-test-id={'projectTimeSaveButton'}
                          onClick={saveHandler}
                        >
                          {t('INPUTS.SAVE')}
                        </MaskOverlaySubmitButton>
                        {item && (
                          <MaskOverlayDeleteButton
                            data-test-id={'projectTimeDelete'}
                            disabled={
                              !(
                                canEditAll ||
                                item.userId === (user as IUser).benutzerCode ||
                                item.userId === (user as User).id
                              )
                            }
                            onClick={deleteHandler}
                          />
                        )}

                        {isCreatedFromTimeTrackingTemplate && (
                          <IconButton
                            onClick={() => {
                              handleDeleteTemplate(createdFromId);
                            }}
                          >
                            <Tooltip
                              title={t('PROJECT_TIME_MASK.REMOVE_TEMPLATE')}
                            >
                              <DeleteIcon />
                            </Tooltip>
                          </IconButton>
                        )}

                        <MaskOverlayMenuWrapper>
                          <MenuItem
                            data-test-id={'projectTimeSettings'}
                            onClick={openSettings}
                          >
                            {t('MASK.SETTINGS')}
                          </MenuItem>
                        </MaskOverlayMenuWrapper>
                      </MaskActions>
                    }
                  />

                  <ProjectTimeMaskContent
                    canChangeUser={canEditAll}
                    isEditMode={!!item}
                    renderCustomFields={customFields.length > 0}
                    startStopwatch={startStopwatchHandler}
                  />
                </Form>
              </div>
            </div>
          </FormProvider>
        </EntityRightsContext.Provider>
      </MaskContextProvider>
    </>
  );
}

const getDurationFromString = (val: string) => {
  let hours: number;
  let minutes: number;
  if (!val.includes(':')) {
    hours = parseInt(val.slice(0, -2), 10) || 0;
    minutes = parseInt(val.slice(-2), 10) || 0;
  } else {
    hours = parseInt(val.split(':')[0], 10) || 0;
    minutes = parseInt(val.split(':')[1], 10) || 0;
  }
  return Duration.fromObject({
    hours,
    minutes,
  });
};

function ProjectTimeMaskContent({
  canChangeUser,
  isEditMode,
  renderCustomFields,
  startStopwatch,
}: {
  canChangeUser: boolean;
  isEditMode: boolean;
  renderCustomFields: boolean;
  startStopwatch: () => void;
}) {
  const { t } = useTranslation();

  const { register, watch, control, getValues, setValue, formState } =
    useFormContextPlus<TimeTrackingFormValue>();

  const rights = useEntityRightsContext();

  const mask = useMaskContext();

  const isManualStartEnd =
    watch('timeTrackingKind') === TimeTrackingKind.VON_BIS;
  const isTrackingKindCheckboxEnabled = rights.create && mask.mode === 'create';

  const startTime = watch('startDateTime');
  const endTime = watch('endDateTime');
  const pauseTime = watch('pause');

  const totalAmount = useMemo(() => {
    return getDuration(startTime, endTime, pauseTime).toFormat('hh:mm');
  }, [endTime, pauseTime, startTime]);

  const isAmountTouched = formState.touchedFields?.amount === true;
  const updateAmountField = useCallback(
    (value: string) => {
      const dur = getDurationFromString(value);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setValue('amount', dur.as('hours'), {
        shouldValidate: isAmountTouched,
        shouldDirty: true,
      });
    },
    [setValue, isAmountTouched]
  );

  const updatePauseField = useCallback(
    (value: string) => {
      const pause = getDurationFromString(value);

      setValue('pause', pause.as('minutes'), {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const projectTimeTrackingShowActivity = useSetting(
    settings.projectTimeTrackingShowActivity()
  );
  const projectTimeTrackingShowContract = useSetting(
    settings.projectTimeTrackingShowContract()
  );

  const projectTimeTrackingShowCustomer = useSetting(
    settings.projectTimeTrackingShowCustomer()
  );
  const projectTimeTrackingShowPosition = useSetting(
    settings.projectTimeTrackingShowPosition()
  );
  const projectTimeTrackingShowProject = useSetting(
    settings.projectTimeTrackingShowProject()
  );
  const projectTimeTrackingShowProjectProcess = useSetting(
    settings.projectTimeTrackingShowProjectProcess()
  );
  const projectTimeTrackingShowTicket = useSetting(
    settings.projectTimeTrackingShowTicket()
  );

  const pickerFromWrapperRef = useRef<HTMLDivElement>(null);

  const projectTimeTrackingCustomerMandatory = useSetting(
    settings.projectTimeTrackingCustomerMandatory()
  );
  const projectTimeTrackingTicketMandatory = useSetting(
    settings.projectTimeTrackingTicketMandatory()
  );
  const projectTimeTrackingProjectMandatory = useSetting(
    settings.projectTimeTrackingProjectMandatory()
  );
  const projectTimeTrackingActivityMandatory = useSetting(
    settings.projectTimeTrackingActivityMandatory()
  );
  const projectTimeTrackingContractMandatory = useSetting(
    settings.projectTimeTrackingContractMandatory()
  );
  const projectTimeTrackingPositionMandatory = useSetting(
    settings.projectTimeTrackingPositionMandatory()
  );
  const projectTimeTrackingProjectProcessMandatory = useSetting(
    settings.projectTimeTrackingProjectProcessMandatory()
  );

  const projectTimeTrackingShowMarkPositionsAsDoneCheckbox = useSetting(
    settings.projectTimeTrackingShowMarkPositionsAsDoneCheckbox()
  );

  const renderCutomerAndWorkers = useCallback(() => {
    const customerControl = (
      <ControllerPlus
        control={control}
        name="customer"
        render={({ field }) => {
          return (
            <CustomerPickerField
              {...field}
              prefilter={[
                {
                  extinct: { $eq: 0 },
                },
              ]}
              label={t('TIME_TRACKING.FIELDS.CUSTOMER')}
              required={projectTimeTrackingCustomerMandatory.value}
            />
          );
        }}
      />
    );

    if (canChangeUser && !isEditMode) {
      const multipleWorkerControl = (
        <ControlWrapper ref={pickerFromWrapperRef}>
          <ControllerPlus
            control={control}
            name="users"
            render={({ field }) => {
              const { value, ...others } = field;
              return (
                <UserPickerField
                  multiple={true}
                  value={value as User[]}
                  {...others}
                  clearable={false}
                  disabled={!canChangeUser}
                />
              );
            }}
          />
        </ControlWrapper>
      );

      return (
        <>
          <ControlWrapper>{multipleWorkerControl}</ControlWrapper>
          {projectTimeTrackingShowCustomer.value && (
            <ControlWrapper>{customerControl}</ControlWrapper>
          )}
        </>
      );
    }

    const workerControl = (
      <ControllerPlus
        control={control}
        name="user"
        render={({ field }) => {
          const { value, ...others } = field;

          return (
            <UserPickerField
              value={value as User}
              {...others}
              clearable={false}
              disabled={!canChangeUser}
            />
          );
        }}
      />
    );

    return (
      <ControlWrapper>
        {projectTimeTrackingShowCustomer.value && customerControl}
        {workerControl}
      </ControlWrapper>
    );
  }, [
    control,
    canChangeUser,
    isEditMode,
    projectTimeTrackingShowCustomer.value,
    projectTimeTrackingCustomerMandatory.value,
    t,
  ]);

  const updateTime = useCallback(
    (value: string) => {
      const start = DateTime.fromISO(getValues('startDateTime'));
      const time = DateTime.fromISO(value);

      return start.set({ hour: time.hour, minute: time.minute }).toISO();
    },
    [getValues]
  );

  const user = useUser();
  const hideContractFooterListButton = !canViewERPPrices(user);
  const shouldUseTimeTrackingQuery = checkUserRight(
    UserRights.MobilNurEigeneVorgaenge,
    user.benutzerRechte
  );

  const customerId = watch('customer')?.id;

  return (
    <div style={{ paddingBottom: '4rem' }}>
      <Collapse title={t('COMMON.TIMETRACKING.TIME_other')} defaultOpen>
        {isManualStartEnd ? (
          <>
            <ControlWrapper>
              <DateTimeInputPicker
                dateLabel={t('INPUTS.DAY')}
                withTime={false}
                clearable={false}
                {...register('startDateTime')}
              />

              <ControlWrapper>
                <ControllerPlus
                  name="startDateTime"
                  control={control}
                  render={({ field }) => {
                    const value = DateTime.fromISO(field.value);

                    return (
                      <TimeInputPicker
                        label={t('INPUTS.START')}
                        value={value}
                        onChange={(e) => {
                          const value = e.currentTarget?.value;

                          if (value) {
                            field.onChange(updateTime(value));
                          }
                        }}
                      />
                    );
                  }}
                />

                <ControllerPlus
                  name="endDateTime"
                  control={control}
                  render={({ field }) => {
                    const value = DateTime.fromISO(field.value);

                    return (
                      <TimeInputPicker
                        label={t('INPUTS.END')}
                        value={value}
                        onChange={(e) => {
                          const value = e.currentTarget?.value;

                          if (value) {
                            field.onChange(updateTime(value));
                          }
                        }}
                      />
                    );
                  }}
                />
              </ControlWrapper>
            </ControlWrapper>

            <ControlWrapper>
              <ControllerPlus
                name="pause"
                control={control}
                render={({ field: pause }) => {
                  const value = Duration.fromObject({
                    minutes: pause.value,
                  }).toFormat('hh:mm');

                  return (
                    <DurationField
                      label={t('INPUTS.PAUSE')}
                      value={value}
                      onChange={updatePauseField}
                      onBlur={(e) => {
                        const start = DateTime.fromISO(
                          getValues('startDateTime')
                        );
                        const end = DateTime.fromISO(getValues('endDateTime'));
                        const netTime = Duration.fromMillis(
                          end.toMillis() - start.toMillis()
                        );
                        const pause = getDurationFromString(e.target.value);

                        if (pause.toMillis() > netTime.toMillis()) {
                          setValue('pause', 0);
                        }
                      }}
                    />
                  );
                }}
              />
              <LabeledInput
                label={t('TIME_TRACKING.FIELDS.AMOUNT')}
                value={totalAmount}
              />
            </ControlWrapper>
          </>
        ) : (
          <ControlWrapper>
            <DateTimeInputPicker
              dateLabel={t('COMMON.DATE')}
              withTime={false}
              clearable={false}
              {...register('date')}
            />
            <ControllerPlus
              name="amount"
              control={control}
              render={({ field: { ref: _ref, ...field } }) => {
                return (
                  <DurationField
                    {...field}
                    label={t('TIME_TRACKING.FIELDS.AMOUNT')}
                    value={decimalToDuration(field.value).toFormat('hh:mm')}
                    onChange={updateAmountField}
                    endAdornment={
                      mask.mode === 'create' ? (
                        <StopwatchIconButton
                          isRunning={false}
                          onStart={startStopwatch}
                        />
                      ) : null
                    }
                  />
                );
              }}
            />
          </ControlWrapper>
        )}

        <ControlWrapper fullWidth={false}>
          <ControllerPlus
            control={control}
            name="timeTrackingKind"
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!isTrackingKindCheckboxEnabled}
                      checked={field.value === TimeTrackingKind.VON_BIS}
                      onChange={(event) => {
                        const kind = event.target.checked
                          ? TimeTrackingKind.VON_BIS
                          : TimeTrackingKind.ANZAHL;

                        field.onChange(kind);
                        localStorage.setItem('TIME_TRACKING_KIND_LS_KEY', kind);
                      }}
                    />
                  }
                  label={t('TIME_TRACKER.MANUAL_LABEL')}
                />
              );
            }}
          />

          <ControllerPlus
            control={control}
            name="isTemplate"
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.checked);
                      }}
                    />
                  }
                  label={t('TIME_TRACKER.SAVE_AS_TEMPLATE')}
                />
              );
            }}
          />
        </ControlWrapper>
      </Collapse>

      <Collapse title={t('COMMON.MISC')} defaultOpen>
        {projectTimeTrackingShowProject.value && (
          <ControlWrapper>
            <ControllerPlus
              control={control}
              name="project"
              render={({ field: projectField }) => {
                const customerFilter = customerId
                  ? [
                      {
                        $and: [
                          {
                            $or: [
                              {
                                customerId: {
                                  $eq: customerId,
                                },
                              },
                              {
                                customerId: { $eq: 0 },
                              },
                            ],
                          },
                        ],
                      },
                    ]
                  : [];

                const sortBy = customerFilter.length ? 'customer.id' : 'name';
                const sortByDirection = customerFilter.length
                  ? SortDirection.DESCENDING
                  : SortDirection.ASCENDING;

                return (
                  <ControllerPlus
                    control={control}
                    name="projectProcess"
                    render={({ field: projectProcessField }) => {
                      return (
                        <MultiStepControls isColumnDirection>
                          <Step index={0} active>
                            <ProjectPickerField
                              {...projectField}
                              useTimeTrackingQuery={shouldUseTimeTrackingQuery}
                              excludeClosedStatus={true}
                              excludeLockInhouseServicesFilter={true}
                              data={{
                                projectStatus: {
                                  id: null,
                                  closedStatus: null,
                                },
                                lockInhouseServices: null,
                              }}
                              prefilter={customerFilter}
                              sortBy={sortBy}
                              sortByDirection={sortByDirection}
                              useSearchHistory={!customerId}
                              clearable
                              label={t('TIME_TRACKING.FIELDS.PROJECT')}
                              required={
                                projectTimeTrackingProjectMandatory.value
                              }
                            />
                          </Step>
                          {projectTimeTrackingShowProjectProcess.value && (
                            <Step index={1} active={projectField.value != null}>
                              {projectField.value != null && (
                                <ProjectProcessPickerField
                                  {...projectProcessField}
                                  projectId={projectField.value.id}
                                  label={t(
                                    'TIME_TRACKING.FIELDS.PROJECTPROCESS'
                                  )}
                                  prefilter={[
                                    {
                                      isClosed: {
                                        $eq: 0,
                                      },
                                    },
                                  ]}
                                  data={{
                                    overBookingPossible: null,
                                    planningAmount: null,
                                    timeTrackings: [
                                      {
                                        id: null,
                                        amount: null,
                                      },
                                    ],
                                    completionFeedbacks: [
                                      {
                                        id: null,
                                        completionPercent: null,
                                      },
                                    ],
                                  }}
                                  required={
                                    projectTimeTrackingProjectProcessMandatory.value
                                  }
                                />
                              )}
                            </Step>
                          )}
                        </MultiStepControls>
                      );
                    }}
                  />
                );
              }}
            />
          </ControlWrapper>
        )}
        {renderCutomerAndWorkers()}
        {projectTimeTrackingShowContract.value && (
          <ControlWrapper>
            <ControllerPlus
              control={control}
              name="contract"
              render={({ field: contractField }) => {
                const projectFilter = watch('project')?.id
                  ? [
                      {
                        projectId: {
                          $eq: watch('project')?.id,
                        },
                      },
                    ]
                  : [];

                const customerFilter =
                  projectFilter.length === 0
                    ? watch('customer')?.id
                      ? [
                          {
                            businessPartnerId: {
                              $eq: watch('customer')?.id,
                            },
                          },
                          {
                            businessPartnerType: {
                              $eq: SdObjType.KUNDE,
                            },
                          },
                        ]
                      : []
                    : [];

                const prefilter = [
                  ...projectFilter,
                  ...customerFilter,
                  {
                    isClosed: { $eq: 0 },
                  },
                  {
                    frozen: {
                      $eq: 0,
                    },
                  },
                ];

                return (
                  <ControllerPlus
                    control={control}
                    name="position"
                    render={({ field: positionField }) => {
                      return (
                        <MultiStepControls isColumnDirection>
                          <Step active={true} index={0}>
                            <ContractPickerField
                              data={CONTRACT_DATA}
                              {...contractField}
                              onChange={(val) => {
                                contractField.onChange(val);
                              }}
                              useTimeTrackingQuery={shouldUseTimeTrackingQuery}
                              prefilter={
                                prefilter.length ? prefilter : undefined
                              }
                              hideFooterListButton={
                                hideContractFooterListButton
                              }
                              label={t('TIME_TRACKING.FIELDS.CONTRACT')}
                              required={
                                projectTimeTrackingContractMandatory.value
                              }
                            />
                          </Step>
                          {projectTimeTrackingShowPosition.value && (
                            <Step active={!!contractField.value} index={1}>
                              <PositionPickerField
                                {...positionField}
                                onChange={(e) => {
                                  positionField.onChange(e);
                                }}
                                positions={
                                  contractField.value?.positionList ?? []
                                }
                                label={t('TIME_TRACKING.FIELDS.POSITION')}
                                required={
                                  projectTimeTrackingPositionMandatory.value
                                }
                              />
                            </Step>
                          )}
                        </MultiStepControls>
                      );
                    }}
                  />
                );
              }}
            />
          </ControlWrapper>
        )}

        {projectTimeTrackingShowPosition.value &&
          projectTimeTrackingShowMarkPositionsAsDoneCheckbox.value &&
          watch('position') && (
            <ControlWrapper>
              <ControllerPlus
                control={control}
                name="shouldMarkPositionDone"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t('CHECK_LIST_POSITIONS_TABLE.MARK_AS_DONE')}
                  />
                )}
              />
            </ControlWrapper>
          )}
        {projectTimeTrackingShowActivity.value && (
          <ControlWrapper>
            <ControllerPlus
              control={control}
              name="activityArticle"
              render={({ field }) => {
                return (
                  <ArticlePickerField
                    {...field}
                    placeholder={t('TIME_TRACKING.FIELDS.ACTIVITYARTICLE')}
                    data={{
                      notdeductable: null,
                    }}
                    useSearchHistory={false}
                    prefilter={[
                      {
                        articleKind: {
                          $eq: ArticleKind.TAETIGKEIT_FUER_ZEITERFASSUNG,
                        },
                      },
                    ]}
                    required={projectTimeTrackingActivityMandatory.value}
                  />
                );
              }}
            />
          </ControlWrapper>
        )}

        {projectTimeTrackingShowTicket.value && (
          <ControlWrapper>
            <ControllerPlus
              control={control}
              name="ticket"
              render={({ field }) => {
                const projectFilter = watch('project')?.id
                  ? [
                      {
                        projectId: {
                          $eq: watch('project')?.id,
                        },
                      },
                    ]
                  : [];

                const customerFilter =
                  projectFilter.length === 0
                    ? watch('customer')?.id
                      ? [
                          {
                            businessPartnerId: {
                              $eq: watch('customer')?.id,
                            },
                          },
                          {
                            businessPartnerType: {
                              $eq: SdObjType.KUNDE,
                            },
                          },
                        ]
                      : []
                    : [];

                const prefilter = [
                  ...customerFilter,
                  ...projectFilter,
                  { status1: { $ne: TicketStatus.ERLEDIGT } },
                ];
                return (
                  <TicketPickerField
                    {...field}
                    prefilter={prefilter}
                    label={t('TIME_TRACKING.FIELDS.TICKET')}
                    data={{
                      projectProcess: {
                        process: null,
                        id: null,
                        overBookingPossible: null,
                        planningAmount: null,
                        timeTrackings: [
                          {
                            id: null,
                            amount: null,
                          },
                        ],
                      },
                    }}
                    required={projectTimeTrackingTicketMandatory.value}
                  />
                );
              }}
            />
          </ControlWrapper>
        )}

        <ControlWrapper>
          <LabeledInput
            multiline
            minRows={3}
            label={t('COMMON.COMMENT')}
            {...register('comment')}
          />
        </ControlWrapper>
      </Collapse>

      {renderCustomFields && <CustomFields columns={6} />}
    </div>
  );
}

const CONTRACT_DATA = {
  project: {
    id: null,
    name: null,
    number: null,
    customer: { id: null, name: null, number: null, website: null },
  },
};

const MISSING_FIELDS_DISPLAY_ORDER: (keyof TimeTrackingFormValue)[] = [
  'amount',
  'project',
  'projectProcess',
  'customer',
  'contract',
  'activityArticle',
  'position',
  'ticket',
];

function sortMissingFields(missingFields: string[]) {
  if (missingFields.length < 2) return missingFields;

  return MISSING_FIELDS_DISPLAY_ORDER.filter((field) =>
    missingFields.includes(field)
  );
}

function getCombinedMissingFields(missingFields: string[]) {
  if (missingFields.length === 1) return missingFields[0];

  const andTranslated = i18next.t('COMMON.AND');

  if (missingFields.length === 2)
    return missingFields.join(` ${andTranslated} `);

  const lastMissingField = missingFields.pop();

  return `${missingFields.join(', ')} ${andTranslated} ${lastMissingField}`;
}
